import Vue from 'vue'
import BookingService from '@/services/BookingService.js'
import BookingGroupService from '@/services/BookingGroupService.js'
import RateService from '@/services/RateService.js'
import { confirmationDialog } from '@/store/confirmation'

export const namespaced = true

export const state = {
  show: false,
  bookingType: 'regular',
  draft: null,
  bookingGroupDraft: null,
  isSaving: false,
  shouldCheckinAfterSave: false,
  isFetchingCharges: false,
  isFetchingRebookingPreview: false,
  hasRebookingPreviewError: false,
  rebookingPreview: null,
  selectedRebookingOption: null,
  latestPricingRequestId: 0,
  activeBookingGroupBookingIndex: 0,
  categoryPickerValue: null,
  isUserSelectedLodgingType: false,
  isAccommodationPriceEditedByUser: false
}

export const mutations = {
  SET_DRAFT (state, val) {
    state.draft = val
  },
  SET_DRAFT_FIELD (state, { field, value }) {
    if (state.draft) {
      state.draft[field] = value
    }
  },
  SET_BOOKING_GROUP_DRAFT (state, val) {
    state.bookingGroupDraft = val
  },
  SET_BOOKING_GROUP_DRAFT_FIELD (state, { field, value }) {
    if (state.bookingGroupDraft) {
      state.bookingGroupDraft[field] = value
    }
  },
  SET_BOOKING_TYPE (state, val) {
    state.bookingType = val
  },
  ADD_BOOKING_GROUP_DRAFT_BOOKING (state, val) {
    if (!state.bookingGroupDraft) {
      return
    }

    const clientIds = state.bookingGroupDraft.bookings.map(booking => booking.clientId)
    val.clientId = Math.max(0, ...clientIds) + 1
    state.bookingGroupDraft.bookings.push(val)
  },
  REMOVE_BOOKING_GROUP_DRAFT_BOOKINGS (state, { start, deleteCount }) {
    if (state.bookingGroupDraft) {
      state.bookingGroupDraft.bookings.splice(start, deleteCount)
    }
  },
  SET_SHOW_FORM_DIALOG (state, val) {
    state.show = val
  },
  SET_IS_SAVING (state, val) {
    state.isSaving = val
  },
  SET_SHOULD_CHECKIN_AFTER_SAVE (state, val) {
    state.shouldCheckinAfterSave = val
  },
  SET_IS_FETCHING_CHARGES (state, val) {
    state.isFetchingCharges = val
  },
  SET_IS_FETCHING_REBOOKING_PREVIEW (state, val) {
    state.isFetchingRebookingPreview = val
  },
  SET_HAS_REBOOKING_PREVIEW_ERROR (state, val) {
    state.hasRebookingPreviewError = val
  },
  SET_REBOOKING_PREVIEW (state, val) {
    state.rebookingPreview = val
  },
  SET_SELECTED_REBOOKING_OPTION (state, val) {
    state.selectedRebookingOption = val
  },
  SET_LATEST_PRICING_REQUEST_ID (state, val) {
    state.latestPricingRequestId = val
  },
  INCREMENT_LATEST_PRICING_REQUEST_ID (state) {
    state.latestPricingRequestId++
  },
  SET_DRAFT_CHARGES (state, val) {
    if (state.draft) {
      state.draft.charges = val
    }
  },
  SET_DRAFT_FEE_OPTIONS (state, val) {
    if (state.draft) {
      Vue.set(state.draft, BOOKING_FIELD_FEE_OPTIONS, val)
    }
  },
  SET_BOOKING_GROUP_CHARGES (state, val) {
    if (state.bookingGroupDraft) {
      state.bookingGroupDraft.charges = val
    }
  },
  SET_BOOKING_GROUP_FEE_OPTIONS (state, val) {
    if (state.bookingGroupDraft) {
      Vue.set(state.bookingGroupDraft, BOOKING_FIELD_FEE_OPTIONS, val)
    }
  },
  SET_ACTIVE_BOOKING_GROUP_BOOKING_INDEX (state, val) {
    state.activeBookingGroupBookingIndex = val
  },
  SET_BOOKING_GROUP_BOOKING_FIELD (state, { bookingClientId, field, value }) {
    const booking = state.bookingGroupDraft?.bookings?.find(booking => booking.clientId === bookingClientId)
    if (booking) {
      booking[field] = value
    }
  },
  SET_BOOKING_GROUP_BOOKINGS_FIELD (state, { field, value }) {
    state.bookingGroupDraft?.bookings.forEach(booking => {
      booking[field] = value
    })
  },
  SET_ACTIVE_BOOKING_FIELD (state, { field, value }) {
    const booking = getActiveBookingDraft(state)
    if (booking) {
      booking[field] = value
    }
  },
  SET_CATEGORY_PICKER_VALUE (state, val) {
    state.categoryPickerValue = val
  },
  SET_IS_USER_SELECTED_LODGING_TYPE (state, val) {
    state.isUserSelectedLodgingType = val
  },
  SET_IS_ACCOMMODATION_PRICE_EDITED_BY_USER (state, val) {
    state.isAccommodationPriceEditedByUser = val
  }
}

export const actions = {
  setDates (context, payload) {
    return Promise.resolve(updateDates(context, payload))
      .then(updateResult => updateResult ? refreshChargesAndAvailability(context) : false)
  },
  setCheckInDate (context, value) {
    return Promise.resolve(updateBookingDate(context, BOOKING_FIELD_CHECK_IN, value))
      .then(updateResult => updateResult ? refreshChargesAndAvailability(context) : false)
  },
  setCheckOutDate (context, value) {
    return Promise.resolve(updateBookingDate(context, BOOKING_FIELD_CHECK_OUT, value))
      .then(updateResult => updateResult ? refreshChargesAndAvailability(context) : false)
  },
  setAdults (context, value) {
    return Promise.resolve(updateActiveBookingField(context, BOOKING_FIELD_ADULTS, value))
      .then(updateResult => updateResult ? refreshChargesAndAvailability(context) : false)
  },
  setChildren (context, value) {
    return Promise.resolve(updateActiveBookingField(context, BOOKING_FIELD_CHILDREN, value))
      .then(updateResult => updateResult ? refreshChargesAndAvailability(context) : false)
  },
  setDogs (context, value) {
    return Promise.resolve(updateActiveBookingField(context, BOOKING_FIELD_DOGS, value))
      .then(updateResult => updateResult ? refreshChargesAndAvailability(context) : false)
  },
  setCancellationPolicy (context, value) {
    const { state, commit } = context
    const updateResult = (() => {
      if (isGroupBookingType(state.bookingType)) {
        if (!hasChangedBookingField(state.bookingGroupDraft, BOOKING_FIELD_CANCELLATION_POLICY, value)) {
          return false
        }

        commit('SET_BOOKING_GROUP_DRAFT_FIELD', { field: BOOKING_FIELD_CANCELLATION_POLICY, value })
        return true
      }

      return updateActiveBookingField(context, BOOKING_FIELD_CANCELLATION_POLICY, value)
    })()

    return Promise.resolve(updateResult)
      .then(updateResult => updateResult ? getCharges(context) : false)
  },
  setAppliedFees (context, value) {
    const { state, commit } = context
    const updateResult = (() => {
      if (isGroupBookingType(state.bookingType)) {
        if (!hasChangedBookingField(state.bookingGroupDraft, BOOKING_FIELD_APPLIED_FEES, value)) {
          return false
        }

        commit('SET_BOOKING_GROUP_DRAFT_FIELD', { field: BOOKING_FIELD_APPLIED_FEES, value })
        return true
      }

      return updateActiveBookingField(context, BOOKING_FIELD_APPLIED_FEES, value)
    })()

    return Promise.resolve(updateResult)
      .then(updateResult => updateResult ? getCharges(context) : false)
  },
  setBookingGroupBookingField (context, payload) {
    const { state, commit } = context
    const booking = state.bookingGroupDraft?.bookings?.find(booking => booking.clientId === payload.bookingClientId)
    if (!booking || !hasChangedBookingField(booking, payload.field, payload.value)) {
      return false
    }

    commit('SET_BOOKING_GROUP_BOOKING_FIELD', payload)
    if ([
      BOOKING_FIELD_CHECK_IN,
      BOOKING_FIELD_CHECK_OUT
    ].includes(payload.field)) {
      updateBookingGroupDateBounds({ state, commit })
    }

    let refresh = payload.refresh ?? null
    if (!refresh && [
      BOOKING_FIELD_ADULTS,
      BOOKING_FIELD_CHILDREN,
      BOOKING_FIELD_DOGS,
      BOOKING_FIELD_CHECK_IN,
      BOOKING_FIELD_CHECK_OUT
    ].includes(payload.field)) {
      refresh = refreshChargesAndAvailability
    }
    if (!refresh && [
      BOOKING_FIELD_APPLIED_FEES,
      BOOKING_FIELD_CANCELLATION_POLICY
    ].includes(payload.field)) {
      refresh = getCharges
    }
    return refresh ? refresh(context) : true
  },
  setSeason (context, season) {
    if (!season) {
      return false
    }

    return Promise.resolve(updateActiveBookingField(context, BOOKING_FIELD_SEASON, season))
      .then(() => updateDates(context, {
        checkIn: season.start,
        checkOut: season.end
      }))
      .then(updateResult => updateResult ? refreshChargesAndAvailability(context) : false)
      .then(() => updateAccommodationPrice(context))
  },
  setCategoryPickerValue (context, category) {
    const { state, rootGetters, commit } = context
    const oldCategory = state.categoryPickerValue
    if (!state.draft || oldCategory?.id === category?.id) {
      return false
    }

    commit('SET_CATEGORY_PICKER_VALUE', category)
    commit('SET_DRAFT_FIELD', {
      field: BOOKING_FIELD_CATEGORY,
      value: category ?? rootGetters['position/activeCategories'][0]
    })

    applyDefaultsForCategory({ state, commit })
    if (category?.id !== state.draft.position?.category_id) {
      commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_POSITION, value: null })
    }

    const updateResult = {
      category: state.draft?.category ?? null,
      selectedPosition: state.draft?.position ?? null
    }

    return Promise.resolve(updateResult)
      .then(updateResult => {
        if (!updateResult) {
          return false
        }

        return Promise.all([
          getCharges(context),
          updateAccommodationPrice(context)
        ]).then(() => updateResult)
      })
  },
  setPosition (context, payload) {
    return Promise.resolve(updatePosition(context, payload))
      .then(updateResult => {
        if (updateResult?.categoryChanged) {
          return Promise.all([
            getCharges(context),
            updateAccommodationPrice(context)
          ]).then(() => updateResult)
        }

        if (!updateResult) {
          return false
        }

        return Promise.resolve(updateAccommodationPrice(context)).then(() => updateResult)
      })
  },
  setBookingGroupPosition (context, payload) {
    return Promise.resolve(updatePosition(context, payload))
      .then(updateResult => {
        if (!updateResult) {
          return false
        }

        return refreshChargesAndAvailability(context).then(() => updateResult)
      })
  },
  setLodgingType (context, lodgingType) {
    return updateActiveBookingField(context, BOOKING_FIELD_LODGING_TYPE, lodgingType)
  },
  markLodgingTypeSelected ({ commit }) {
    commit('SET_IS_USER_SELECTED_LODGING_TYPE', true)
    return true
  },
  setCustomer (context, customer) {
    const { state, commit } = context
    let customerChanged = false
    if (isGroupBookingType(state.bookingType)) {
      if (hasChangedBookingField(state.bookingGroupDraft, BOOKING_FIELD_CUSTOMER, customer)) {
        commit('SET_BOOKING_GROUP_DRAFT_FIELD', { field: BOOKING_FIELD_CUSTOMER, value: customer })
        customerChanged = true
      }
    } else if (hasChangedBookingField(state.draft, BOOKING_FIELD_CUSTOMER, customer)) {
      commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_CUSTOMER, value: customer })
      customerChanged = true
    }

    const cardNumberChanged = setDraftCkeCardNumber(context, customer?.cke_card_number ?? null)
    return customerChanged || cardNumberChanged
  },
  setPendingCardNumber (context, cardNumber) {
    return setDraftCkeCardNumber(context, cardNumber ?? null)
  },
  setBookingGroupName ({ state, commit }, name) {
    if (!hasChangedBookingField(state.bookingGroupDraft, BOOKING_FIELD_NAME, name)) {
      return false
    }

    commit('SET_BOOKING_GROUP_DRAFT_FIELD', { field: BOOKING_FIELD_NAME, value: name })
    return true
  },
  setArrivalTime (context, value) {
    const { state } = context
    if (isGroupBookingType(state.bookingType)) {
      return false
    }
    return updateActiveBookingField(context, 'expected_arrival_time', value)
  },
  setAccommodationPrice (context, value) {
    return updateActiveBookingField(context, BOOKING_FIELD_ACCOMMODATION_PRICE, value)
  },
  markAccommodationPriceEdited ({ commit }) {
    commit('SET_IS_ACCOMMODATION_PRICE_EDITED_BY_USER', true)
    return true
  },
  setSameCancellationPolicyForAllBookings ({ state, commit }, value) {
    if (isGroupBookingType(state.bookingType)) {
      if (!hasChangedBookingField(state.bookingGroupDraft, BOOKING_FIELD_SAME_CANCELLATION_POLICY_FOR_ALL_BOOKINGS, value)) {
        return false
      }

      commit('SET_BOOKING_GROUP_DRAFT_FIELD', {
        field: BOOKING_FIELD_SAME_CANCELLATION_POLICY_FOR_ALL_BOOKINGS,
        value
      })
      return true
    }
    return false
  },
  setSameDatesForAllBookings (context, value) {
    const { state, commit } = context
    if (!isGroupBookingType(state.bookingType)) {
      return false
    }

    if (!hasChangedBookingField(state.bookingGroupDraft, BOOKING_FIELD_SAME_DATES_FOR_ALL_BOOKINGS, value)) {
      return false
    }

    commit('SET_BOOKING_GROUP_DRAFT_FIELD', {
      field: BOOKING_FIELD_SAME_DATES_FOR_ALL_BOOKINGS,
      value
    })

    if (value) {
      commit('SET_BOOKING_GROUP_BOOKINGS_FIELD', { field: BOOKING_FIELD_CHECK_IN, value: state.bookingGroupDraft.checkIn })
      commit('SET_BOOKING_GROUP_BOOKINGS_FIELD', { field: BOOKING_FIELD_CHECK_OUT, value: state.bookingGroupDraft.checkOut })
    } else {
      updateBookingGroupDateBounds({ state, commit })
    }

    return refreshChargesAndAvailability(context)
  },
  setActiveBookingGroupBookingIndex (context, index) {
    const { state, commit } = context
    if (state.activeBookingGroupBookingIndex === index) {
      return false
    }

    commit('SET_ACTIVE_BOOKING_GROUP_BOOKING_INDEX', index)
    return refreshChargesAndAvailability(context)
  },
  normalizeActiveBookingGroupBookingIndex ({ state, commit }) {
    if (isActiveBookingGroupBookingIndexOutOfBounds(state)) {
      commit('SET_ACTIVE_BOOKING_GROUP_BOOKING_INDEX', 0)
      return true
    }
    return false
  },
  setShowFormDialog ({ commit }, value) {
    commit('SET_SHOW_FORM_DIALOG', value)
    return true
  },
  afterFormOpened (context) {
    context.commit('SET_IS_USER_SELECTED_LODGING_TYPE', context.getters.isExistingBooking)
    return refreshChargesAndAvailability(context)
  },
  afterFormClosed ({ commit, dispatch, rootGetters }) {
    commit('SET_IS_ACCOMMODATION_PRICE_EDITED_BY_USER', false)
    commit('SET_CATEGORY_PICKER_VALUE', null)
    commit('SET_IS_USER_SELECTED_LODGING_TYPE', false)
    resetPricingState(commit)
    commit('SET_BOOKING_TYPE', REGULAR_BOOKING)
    commit('SET_DRAFT', null)
    commit('SET_BOOKING_GROUP_DRAFT', null)

    if (rootGetters[SCR_IS_ENABLED_GETTER]) {
      return dispatch('scr/clearSearch', null, { root: true })
    }

    return true
  },
  setBookingType (context, bookingType) {
    const { state, commit } = context
    const oldBookingType = state.bookingType
    commit('SET_BOOKING_TYPE', bookingType)

    if (bookingType === oldBookingType || !state.show) {
      return Promise.resolve(false)
    }

    let change
    if (isSeasonBookingType(bookingType)) {
      change = ensureBookingDraft(context, SEASON_BOOKING)
        .then(() => {
          commit('SET_BOOKING_GROUP_DRAFT', null)
          commit('SET_DRAFT_CHARGES', [])
          commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_TYPE, value: SEASON_BOOKING })
          commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_APPLIED_FEES, value: [] })
          return true
        })
    } else if (isGroupBookingType(bookingType) && !state.bookingGroupDraft) {
      change = ensureBookingDraft(context, REGULAR_BOOKING)
        .then(sourceDraft => {
          return startNewBookingGroupForm(context, {
            customer: sourceDraft.customer,
            checkIn: sourceDraft.checkIn,
            checkOut: sourceDraft.checkOut
          }).then(() => true)
        })
    } else if (isRegularBookingType(bookingType)) {
      change = ensureBookingDraft(context, REGULAR_BOOKING)
        .then(() => {
          commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_TYPE, value: REGULAR_BOOKING })
          commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_SEASON, value: null })
          commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_ACCOMMODATION_PRICE, value: 0 })
          commit('SET_BOOKING_GROUP_DRAFT', null)
          return true
        })
    } else {
      change = Promise.resolve(false)
    }

    return change
      .then(changed => {
        if (!changed) {
          return false
        }

        resetPricingState(context.commit)
        return refreshChargesAndAvailability(context)
      })
  },
  startNewSeasonBooking: context => startNewBookingForm(context, { type: SEASON_BOOKING }),
  startNewBookingGroup: startNewBookingGroupForm,
  setBookingGroupBookingsCount (context, count) {
    const { state, commit } = context
    if (!state.bookingGroupDraft) {
      return false
    }

    const bookingCount = Math.max(1, count)
    const diff = bookingCount - (state.bookingGroupDraft.bookings?.length ?? 0)
    if (diff > 0) {
      return addBookingDraftsToBookingGroup(context, diff)
    }

    if (diff < 0) {
      commit('REMOVE_BOOKING_GROUP_DRAFT_BOOKINGS', { start: bookingCount, deleteCount: Math.abs(diff) })
      updateBookingGroupDateBounds({ state, commit })
      return refreshChargesAndAvailability(context)
    }

    return false
  },
  removeBookingDraftFromBookingGroup (context, index) {
    const { state, commit } = context
    const bookingDraft = state.bookingGroupDraft?.bookings?.[index]
    if (!bookingDraft) {
      return false
    }

    const bookingCount = state.bookingGroupDraft?.bookings?.length ?? 0
    if (bookingCount <= 1) {
      return false
    }

    if (state.activeBookingGroupBookingIndex >= index) {
      commit('SET_ACTIVE_BOOKING_GROUP_BOOKING_INDEX', Math.max(0, bookingCount - 2))
    }
    commit('REMOVE_BOOKING_GROUP_DRAFT_BOOKINGS', { start: index, deleteCount: 1 })
    updateBookingGroupDateBounds({ state, commit })
    return refreshChargesAndAvailability(context)
  },
  startNewBooking: startNewBookingForm,
  startEditBooking ({ commit }, originalBooking) {
    const booking = { ...originalBooking }
    const checkIn = booking.check_in.clone()
    const checkOut = booking.check_out.clone()
    const isBookingFeeKnown = Object.prototype.hasOwnProperty.call(booking, 'applied_fees') ||
      Object.prototype.hasOwnProperty.call(booking, 'has_booking_fee')

    Vue.set(booking, BOOKING_FIELD_CHECK_IN, checkIn)
    Vue.set(booking, BOOKING_FIELD_CHECK_OUT, checkOut)
    Vue.set(booking, BOOKING_FIELD_ACCOMMODATION_PRICE, booking.total_accommodation_amount)
    Vue.set(booking, 'charges', [])
    Vue.set(booking, 'isBookingFeeKnown', isBookingFeeKnown)
    Vue.set(booking, BOOKING_FIELD_APPLIED_FEES, booking.applied_fees ?? appliedFeesForBooking(booking))

    commit('SET_BOOKING_TYPE', booking.type === SEASON_BOOKING ? SEASON_BOOKING : REGULAR_BOOKING)
    commit('SET_DRAFT', booking)
    commit('SET_BOOKING_GROUP_DRAFT', null)
    commit('SET_SHOW_FORM_DIALOG', true)
    return true
  },
  setSelectedRebookingOption ({ commit }, val) {
    commit('SET_SELECTED_REBOOKING_OPTION', val)
    return true
  },
  saveForm ({ state, rootState, commit, dispatch }, { withCheckin = false } = {}) {
    if (state.isSaving || state.isFetchingCharges || state.isFetchingRebookingPreview) {
      return false
    }

    commit('SET_SHOULD_CHECKIN_AFTER_SAVE', withCheckin)

    if (isGroupBookingType(state.bookingType)) {
      let errorMessage = null
      if (!state.bookingGroupDraft?.customer) {
        errorMessage = REQUIRED_CUSTOMER_MESSAGE
      } else if (state.bookingGroupDraft.bookings.some(booking => !booking.category)) {
        errorMessage = REQUIRED_POSITION_MESSAGE
      }

      if (errorMessage) {
        dispatch(CREATE_SNACKBAR_ACTION, {
          color: 'error',
          text: errorMessage
        }, { root: true })
        return false
      }

      commit('SET_IS_SAVING', true)
      return BookingGroupService.saveBookingGroup({
        bookingGroup: state.bookingGroupDraft,
        displayedTotal: bookingFormChargeTotal(state)
      })
        .then(({ data }) => {
          if (data?.status !== 'success') {
            return false
          }

          commit('SET_SHOW_FORM_DIALOG', false)
          commit(TRIGGER_BOOKING_UPDATED_MUTATION, null, { root: true })
          return dispatch('bookingGroup/openDialogById', {
            bookingGroupId: data.data.booking_group_id
          }, { root: true })
        })
        .finally(() => {
          commit('SET_IS_SAVING', false)
        })
    }

    let errorMessage = null
    if (!state.draft?.customer) {
      errorMessage = REQUIRED_CUSTOMER_MESSAGE
    } else if (!state.draft.category) {
      errorMessage = REQUIRED_POSITION_MESSAGE
    } else if (state.draft.type === SEASON_BOOKING && !state.draft.position) {
      errorMessage = REQUIRED_POSITION_MESSAGE
    }

    if (errorMessage) {
      dispatch(CREATE_SNACKBAR_ACTION, {
        color: 'error',
        text: errorMessage
      }, { root: true })
      return false
    }

    if (isExistingFormDraft(state)) {
      return saveExistingBookingForm({ state, commit, dispatch })
    }

    return saveNewBookingForm({ state, rootState, commit, dispatch })
  }
}

export const getters = {
  activeBooking: (state) => {
    return getActiveBookingDraft(state)
  },
  availableCancellationPolicies: (state, getters) => {
    return bookingAvailableCancellationPolicies(getters.activeBooking)
  },
  bookingGroupBookingsCount: (state) => {
    return state.bookingGroupDraft?.bookings?.length ?? 0
  },
  bookingTypeOptions: () => {
    return [
      {
        value: REGULAR_BOOKING,
        text: 'Vanlig bokning'
      },
      {
        value: GROUP_BOOKING,
        text: 'Gruppbokning'
      },
      {
        value: SEASON_BOOKING,
        text: 'Säsongsbokning'
      }
    ]
  },
  charges: (state) => {
    return isGroupBookingType(state.bookingType)
      ? state.bookingGroupDraft?.charges ?? null
      : state.draft?.charges ?? null
  },
  chargeTotal: (state) => {
    return bookingFormChargeTotal(state)
  },
  checkInDate: (state) => {
    return getFormDraft(state)?.checkIn
  },
  checkOutDate: (state) => {
    return getFormDraft(state)?.checkOut
  },
  customer: (state) => {
    return getFormDraft(state)?.customer
  },
  dialogHeader: (state, getters) => {
    return getters.isExistingBooking ? 'Redigera bokning' : 'Ny bokning'
  },
  hasAvailabilityError: (state, getters, rootState) => {
    return hasMatchingAvailabilityRequest(
      rootState.position.availabilitiesErrors,
      getters.activeBooking,
      {
        checkIn: getters.checkInDate,
        checkOut: getters.checkOutDate,
        isBookingGroup: getters.isBookingGroup
      }
    )
  },
  hasBookingFeeOption: (state) => {
    return bookingFormFeeOptions(state).some(feeOption => feeOption.key === BOOKING_FEE)
  },
  isActiveBookingExisting: (state) => {
    return !!getActiveBookingDraft(state)?.id
  },
  isBookVisitBooking: (state) => {
    return !!getActiveBookingDraft(state)?.bookvisit_booking_code
  },
  isBookingGroup: (state) => {
    return isGroupBookingType(state.bookingType)
  },
  isDropInBooking: (state) => {
    return isDropInFormDraft(state)
  },
  isExistingBooking: (state) => {
    return isExistingFormDraft(state)
  },
  isLoadingAvailability: (state, getters, rootState) => {
    return hasMatchingAvailabilityRequest(
      rootState.position.fetchingAvailabilities,
      getters.activeBooking,
      {
        checkIn: getters.checkInDate,
        checkOut: getters.checkOutDate,
        isBookingGroup: getters.isBookingGroup
      }
    )
  },
  isRegularBooking: (state) => {
    return isRegularBookingType(state.bookingType)
  },
  isSeasonBooking: (state) => {
    return isSeasonBookingType(state.bookingType)
  },
  mapImageAspectRatio: (state, getters, rootState) => {
    return rootState.sessionCache.mapImageAspectRatio
  },
  saveBookingButtonText: (state, getters, rootState) => {
    return bookingFormShouldSendBookingConfirmation(state, rootState) && !getters.isBookingGroup
      ? 'Spara & bekräfta'
      : 'Spara bokning'
  },
  scrEnabled: (state, getters, rootState, rootGetters) => {
    return rootGetters[SCR_IS_ENABLED_GETTER]
  },
  selectedPositionIds: (state) => {
    return getSelectedPositionIds(state)
  },
  settingAlwaysShowMap: (state, getters, rootState) => {
    return rootState.localSettings.bookingFormAlwaysShowMap
  },
  settingCategoryFilterDropdown: (state, getters, rootState) => {
    return rootState.localSettings.bookingFormCategoryFilterDropdown
  },
  showCancellationPolicyPicker: (state, getters) => {
    return !!getters.activeBooking?.category && getters.availableCancellationPolicies.length > 1
  },
  showLodgingTypePicker: (state, getters) => {
    return (getters.activeBooking?.category?.lodgingTypes?.length ?? 0) > 1
  }
}

// Constants and helpers

const REGULAR_BOOKING = 'regular'
const GROUP_BOOKING = 'group'
const SEASON_BOOKING = 'season'
const BOOKING_FEE = 'booking_fee'
const POSITION_RESERVATION_FEE = 'position_reservation_fee'
const ELECTRICITY_FEE = 'electricity_fee'

function isRegularBookingType (bookingType) {
  return bookingType === REGULAR_BOOKING
}

function isGroupBookingType (bookingType) {
  return bookingType === GROUP_BOOKING
}

function isSeasonBookingType (bookingType) {
  return bookingType === SEASON_BOOKING
}

function standardCancellationPoliciesForCategory (category) {
  if (!category) {
    return []
  }

  return category.cancellationPolicies.filter(policy => policy.available_on.includes('reception_standard'))
}

function seasonCancellationPoliciesForBooking (booking) {
  if (!booking?.category) {
    return []
  }

  return booking.category.cancellationPolicies.filter(policy => policy.available_on.includes('reception_season'))
}

function bookingAvailableCancellationPolicies (booking) {
  return booking?.type === SEASON_BOOKING
    ? seasonCancellationPoliciesForBooking(booking)
    : standardCancellationPoliciesForCategory(booking?.category)
}

function appliedFeesForBooking (booking) {
  const appliedFees = []
  if (booking?.has_booking_fee) {
    appliedFees.push(BOOKING_FEE)
  }
  if (booking?.has_position_reservation) {
    appliedFees.push(POSITION_RESERVATION_FEE)
  }
  if (booking?.has_electricity_fee) {
    appliedFees.push(ELECTRICITY_FEE)
  }
  return appliedFees
}

const BOOKING_FIELD_ACCOMMODATION_PRICE = 'accommodation_price'
const BOOKING_FIELD_ADULTS = 'adults'
const BOOKING_FIELD_APPLIED_FEES = 'appliedFees'
const BOOKING_FIELD_CANCELLATION_POLICY = 'cancellationPolicy'
const BOOKING_FIELD_CATEGORY = 'category'
const BOOKING_FIELD_CHECK_IN = 'checkIn'
const BOOKING_FIELD_CHECK_OUT = 'checkOut'
const BOOKING_FIELD_CHILDREN = 'children'
const BOOKING_FIELD_CKE_CARD_NUMBER = 'cke_card_number'
const BOOKING_FIELD_CUSTOMER = 'customer'
const BOOKING_FIELD_DOGS = 'dogs'
const BOOKING_FIELD_FEE_OPTIONS = 'feeOptions'
const BOOKING_FIELD_LODGING_TYPE = 'lodgingType'
const BOOKING_FIELD_NAME = 'name'
const BOOKING_FIELD_POSITION = 'position'
const BOOKING_FIELD_SAME_CANCELLATION_POLICY_FOR_ALL_BOOKINGS = 'sameCancellationPolicyForAllBookings'
const BOOKING_FIELD_SAME_DATES_FOR_ALL_BOOKINGS = 'sameDatesForAllBookings'
const BOOKING_FIELD_SEASON = 'season'
const BOOKING_FIELD_TYPE = 'type'

function getActiveBookingDraft (state) {
  if (isGroupBookingType(state.bookingType)) {
    return state.bookingGroupDraft?.bookings?.[state.activeBookingGroupBookingIndex] ?? null
  }
  return state.draft
}

function getFormDraft (state) {
  return isGroupBookingType(state.bookingType)
    ? state.bookingGroupDraft
    : state.draft
}

function isExistingFormDraft (state) {
  if (isGroupBookingType(state.bookingType)) {
    return !!state.bookingGroupDraft?.id
  }

  return !!getActiveBookingDraft(state)?.id
}

function isDropInFormDraft (state) {
  const today = window.dayjs.today()
  const activeBooking = getActiveBookingDraft(state)
  const checkInDate = getFormDraft(state)?.checkIn

  return !!activeBooking &&
    !!checkInDate &&
    checkInDate.isSameOrBefore(today) &&
    !isExistingFormDraft(state) &&
    !isSeasonBookingType(state.bookingType)
}

function availabilityDraftBookings (state, activeBooking = null) {
  if (isGroupBookingType(state.bookingType)) {
    return state.bookingGroupDraft?.bookings ?? []
  }

  const booking = activeBooking ?? state.draft
  return booking ? [booking] : []
}

function getSelectedPositionIds (state) {
  return availabilityDraftBookings(state)
    .map(booking => booking.position ? booking.position.id : null)
    .filter(id => id !== null)
}

function bookingAvailabilityParams (booking, { checkIn, checkOut }) {
  return {
    checkIn,
    checkOut,
    bookingId: booking.id ?? 0,
    occupancy: booking ? booking.adults + booking.children : null,
    dogs: booking.dogs
  }
}

function hasMatchingAvailabilityRequest (requests, booking, { checkIn, checkOut, isBookingGroup }) {
  if (!booking || !checkIn || !checkOut) {
    return false
  }

  return !!requests.find(request => {
    const params = bookingAvailabilityParams(booking, { checkIn, checkOut })
    if (!hasSameAvailabilityBaseParams(request, params)) {
      return false
    }

    // Har ingen occupancy eller dogs pa bookingGroup
    return isBookingGroup || matchesAvailabilityParams(request, params)
  })
}

function isActiveBookingGroupBookingIndexOutOfBounds (state) {
  const bookings = state.bookingGroupDraft?.bookings ?? []
  return isGroupBookingType(state.bookingType) &&
    !bookings[state.activeBookingGroupBookingIndex] &&
    state.activeBookingGroupBookingIndex >= bookings.length
}

function matchesAvailabilityParams (request, params) {
  return request.checkIn.isSame(params.checkIn, 'date') &&
    request.checkOut.isSame(params.checkOut, 'date') &&
    request.bookingId === params.bookingId &&
    request.occupancy === params.occupancy &&
    request.dogs === params.dogs
}

function hasSameAvailabilityBaseParams (request, params) {
  return request.checkIn.isSame(params.checkIn, 'date') &&
    request.checkOut.isSame(params.checkOut, 'date') &&
    request.bookingId === params.bookingId
}

// Field updates and availability refreshes

function updateDates (context, { checkIn, checkOut }) {
  return Promise.all([
    updateBookingDate(context, BOOKING_FIELD_CHECK_IN, checkIn),
    updateBookingDate(context, BOOKING_FIELD_CHECK_OUT, checkOut)
  ]).then(results => results.some(Boolean))
}

function updatePosition (context, { position, bookingClientId, setCategory = true }) {
  const { state, commit } = context
  const booking = getActiveBookingDraft(state)

  if (!booking) {
    return false
  }

  if (!activateBookingGroupBooking({ state, commit }, bookingClientId)) {
    return false
  }

  const activeBooking = getActiveBookingDraft(state)

  if (hasGroupPositionConflict(state, position, activeBooking)) {
    return false
  }

  if (!position) {
    commit('SET_CATEGORY_PICKER_VALUE', null)
    commit('SET_ACTIVE_BOOKING_FIELD', { field: BOOKING_FIELD_CATEGORY, value: null })
    commit('SET_ACTIVE_BOOKING_FIELD', { field: BOOKING_FIELD_POSITION, value: null })
    return {
      position: null,
      categoryChanged: true
    }
  }

  const categoryChanged = !activeBooking.category || activeBooking.category.id !== position.category.id
  if (categoryChanged) {
    commit('SET_ACTIVE_BOOKING_FIELD', { field: BOOKING_FIELD_CATEGORY, value: position.category })
    applyDefaultsForCategory(context)
    commit('SET_ACTIVE_BOOKING_FIELD', { field: BOOKING_FIELD_POSITION, value: position })
    if (setCategory) {
      commit('SET_CATEGORY_PICKER_VALUE', position.category)
    }
    return {
      position,
      categoryChanged
    }
  }

  commit('SET_ACTIVE_BOOKING_FIELD', { field: BOOKING_FIELD_POSITION, value: position })
  if (setCategory) {
    commit('SET_CATEGORY_PICKER_VALUE', position.category)
  }

  return {
    position,
    categoryChanged
  }
}

function applyDefaultsForCategory ({ state, commit }) {
  const booking = getActiveBookingDraft(state)

  if (!booking?.category) {
    return false
  }

  if (shouldUseDefaultLodgingType(booking, state.isUserSelectedLodgingType)) {
    commit('SET_ACTIVE_BOOKING_FIELD', {
      field: BOOKING_FIELD_LODGING_TYPE,
      value: booking.category.defaultLodgingType
    })
    commit('SET_IS_USER_SELECTED_LODGING_TYPE', false)
  }

  if (shouldUseDefaultCancellationPolicy(booking)) {
    commit('SET_ACTIVE_BOOKING_FIELD', {
      field: BOOKING_FIELD_CANCELLATION_POLICY,
      value: bookingAvailableCancellationPolicies(booking)[0] ?? null
    })
  }

  return true
}

function updateAccommodationPrice (context) {
  const { state } = context
  if (state.isAccommodationPriceEditedByUser) {
    return Promise.resolve(false)
  }

  const booking = getActiveBookingDraft(state)
  if (!isSeasonBookingType(state.bookingType) || !booking?.season) {
    return updateActiveBookingField(context, BOOKING_FIELD_ACCOMMODATION_PRICE, 0)
  }

  const season = booking?.season

  if (!season) {
    return updateActiveBookingField(context, BOOKING_FIELD_ACCOMMODATION_PRICE, 0)
  }

  const positionPrice = booking.position && booking.position.id !== 0
    ? season.custom_season_prices.find(seasonPrice => seasonPrice.position_ids.includes(booking.position.id))
    : null

  if (positionPrice) {
    return updateSeasonAccommodationPrice(context, positionPrice.price)
  }

  const categoryPrice = booking.category && booking.category.id !== 0
    ? season.custom_season_prices.find(seasonPrice => seasonPrice.category_ids.includes(booking.category.id))
    : null

  return updateSeasonAccommodationPrice(context, categoryPrice?.price ?? season.default_season_price)
}

function updateSeasonAccommodationPrice (context, price) {
  const { state, commit } = context
  const booking = getActiveBookingDraft(state)

  if (!booking?.id || booking.accommodation_price === price) {
    return updateActiveBookingField(context, BOOKING_FIELD_ACCOMMODATION_PRICE, price)
  }

  confirmationDialog({
    header: 'Uppdatera säsongspris',
    body: `Vill du ersätta bokningens nuvarande platsavgift ${formatBookingFormAmount(booking.accommodation_price)} kr med priset ${formatBookingFormAmount(price)} kr för vald säsong och plats?`,
    confirmFn: () => {
      commit('SET_ACTIVE_BOOKING_FIELD', {
        field: BOOKING_FIELD_ACCOMMODATION_PRICE,
        value: price
      })
    }
  })

  return Promise.resolve(false)
}

function formatBookingFormAmount (amount) {
  return ((amount ?? 0) / 100).toLocaleString('sv-SE')
}

function setDraftCkeCardNumber ({ state, commit }, cardNumber) {
  if (!hasChangedBookingField(state.draft, BOOKING_FIELD_CKE_CARD_NUMBER, cardNumber)) {
    return false
  }

  commit('SET_DRAFT_FIELD', { field: BOOKING_FIELD_CKE_CARD_NUMBER, value: cardNumber })
  return true
}

function updateBookingDate ({ state, commit }, field, value) {
  if (isGroupBookingType(state.bookingType)) {
    if (!hasChangedBookingField(state.bookingGroupDraft, field, value)) {
      return false
    }

    commit('SET_BOOKING_GROUP_DRAFT_FIELD', { field, value })
    commit('SET_BOOKING_GROUP_BOOKINGS_FIELD', { field, value })
    return true
  }

  if (!hasChangedBookingField(state.draft, field, value)) {
    return false
  }

  commit('SET_DRAFT_FIELD', { field, value })
  return true
}

function updateBookingGroupDateBounds ({ state, commit }) {
  const bounds = bookingGroupDateBounds(state.bookingGroupDraft?.bookings ?? [])
  if (!bounds) {
    return false
  }

  commit('SET_BOOKING_GROUP_DRAFT_FIELD', { field: BOOKING_FIELD_CHECK_IN, value: bounds.checkIn })
  commit('SET_BOOKING_GROUP_DRAFT_FIELD', { field: BOOKING_FIELD_CHECK_OUT, value: bounds.checkOut })
  return true
}

function bookingGroupDateBounds (bookings) {
  const bookingsWithDates = bookings.filter(booking => booking.checkIn && booking.checkOut)
  if (bookingsWithDates.length === 0) {
    return null
  }

  return {
    checkIn: bookingsWithDates.reduce((firstDate, booking) => booking.checkIn.isBefore(firstDate) ? booking.checkIn : firstDate, bookingsWithDates[0].checkIn),
    checkOut: bookingsWithDates.reduce((lastDate, booking) => booking.checkOut.isAfter(lastDate) ? booking.checkOut : lastDate, bookingsWithDates[0].checkOut)
  }
}

function activateBookingGroupBooking ({ state, commit }, bookingClientId) {
  if (!isGroupBookingType(state.bookingType) || !bookingClientId) {
    return true
  }

  const index = state.bookingGroupDraft?.bookings?.findIndex(booking => booking.clientId === bookingClientId) ?? -1
  if (index < 0) {
    return false
  }

  commit('SET_ACTIVE_BOOKING_GROUP_BOOKING_INDEX', index)
  return true
}

function hasGroupPositionConflict (state, position, activeBooking) {
  if (!isGroupBookingType(state.bookingType) || !position || position.id === 0) {
    return false
  }

  return state.bookingGroupDraft?.bookings?.some(booking => {
    return booking !== activeBooking &&
      booking.position?.id === position.id &&
      bookingDatesOverlap(booking, activeBooking)
  }) ?? false
}

function bookingDatesOverlap (booking, otherBooking) {
  if (!booking?.checkIn || !booking?.checkOut || !otherBooking?.checkIn || !otherBooking?.checkOut) {
    return true
  }

  return booking.checkIn.isBefore(otherBooking.checkOut, 'date') &&
    booking.checkOut.isAfter(otherBooking.checkIn, 'date')
}

function shouldUseDefaultLodgingType (booking, isUserSelectedLodgingType) {
  if (!booking.lodgingType || !isUserSelectedLodgingType) {
    return true
  }

  return !booking.category.lodgingTypes.some(lodgingType => lodgingType.id === booking.lodgingType.id)
}

function shouldUseDefaultCancellationPolicy (booking) {
  if (!booking.cancellationPolicy) {
    return true
  }

  return !bookingAvailableCancellationPolicies(booking).some(policy => policy.id === booking.cancellationPolicy.id)
}

function fetchAvailabilityForBooking (dispatch, booking, getters) {
  // Skickar bara requests om inte redan hamtat/skickat request med samma parametrar, sa gor inget att kors flera ganger
  return dispatch('position/fetchAvailability', bookingAvailabilityParams(booking, {
    checkIn: booking.checkIn ?? getters.checkInDate,
    checkOut: booking.checkOut ?? getters.checkOutDate
  }), { root: true })
}

function refreshChargesAndAvailability (context) {
  return Promise.all([
    getCharges(context),
    fetchAvailability(context)
  ])
}

function fetchAvailability ({ state, getters, dispatch }) {
  return Promise.all(
    availabilityDraftBookings(state, getters.activeBooking)
      .map(booking => fetchAvailabilityForBooking(dispatch, booking, getters))
  )
}

function updateActiveBookingField ({ state, commit }, field, value) {
  const booking = getActiveBookingDraft(state)
  if (!hasChangedBookingField(booking, field, value)) {
    return false
  }

  commit('SET_ACTIVE_BOOKING_FIELD', { field, value })
  return true
}

function hasChangedBookingField (booking, field, value) {
  return !!booking && !hasSameBookingFieldValue(booking[field], value)
}

function hasSameBookingFieldValue (oldValue, newValue) {
  if (oldValue === newValue) {
    return true
  }

  if (oldValue?.isSame && newValue?.isSame) {
    return oldValue.isSame(newValue)
  }

  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    return hasSameArrayValues(oldValue, newValue)
  }

  return false
}

function hasSameArrayValues (oldValue, newValue) {
  return oldValue.length === newValue.length &&
    oldValue.every((item, index) => item === newValue[index])
}

// Form lifecycle and draft creation

const POSITION_DEFAULT_BOOKING_CATEGORY_GETTER = 'position/defaultBookingCategory'
const SCR_IS_ENABLED_GETTER = 'scr/isEnabled'

function startNewBookingForm ({ commit, rootGetters }, attributes) {
  const defaultCategory = rootGetters[POSITION_DEFAULT_BOOKING_CATEGORY_GETTER]
  const draft = createBookingDraft(defaultCategory, attributes)

  commit('SET_BOOKING_TYPE', draft.type)
  commit('SET_DRAFT', draft)
  commit('SET_BOOKING_GROUP_DRAFT', null)
  commit('SET_SHOW_FORM_DIALOG', true)
  return Promise.resolve(true)
}

function startNewBookingGroupForm ({ commit, rootGetters }, attributes) {
  const defaultCategory = rootGetters[POSITION_DEFAULT_BOOKING_CATEGORY_GETTER]
  const bookingGroupDraft = createBookingGroupDraft(defaultCategory, attributes)

  commit('SET_BOOKING_TYPE', GROUP_BOOKING)
  commit('SET_DRAFT', null)
  commit('SET_BOOKING_GROUP_DRAFT', bookingGroupDraft)
  commit('SET_SHOW_FORM_DIALOG', true)
  return Promise.resolve(true)
}

function addBookingDraftsToBookingGroup (context, count) {
  const { state, commit, rootGetters } = context
  if (!state.bookingGroupDraft) {
    return false
  }

  const defaultCategory = rootGetters[POSITION_DEFAULT_BOOKING_CATEGORY_GETTER]
  for (let i = 0; i < count; i++) {
    const bookingDraft = createBookingDraft(defaultCategory)
    if (state.bookingGroupDraft.sameDatesForAllBookings) {
      bookingDraft.checkIn = state.bookingGroupDraft.checkIn.clone()
      bookingDraft.checkOut = state.bookingGroupDraft.checkOut.clone()
    }
    bookingDraft.position = defaultCategory?.positions?.[0] || null
    commit('ADD_BOOKING_GROUP_DRAFT_BOOKING', bookingDraft)
  }
  return refreshChargesAndAvailability(context)
}

function createBookingDraft (defaultCategory, attributes = {}) {
  const defaultCancellationPolicy = standardCancellationPoliciesForCategory(defaultCategory)[0] || null
  const draft = {
    id: 0,
    clientId: 1,
    customer: null,
    position: {
      full_name: defaultCategory?.name || '',
      id: 0,
      category: defaultCategory,
      avatar: {
        icon: 'mdi-map-marker-question',
        text: '',
        color: 'blue accent-1'
      }
    },
    category: defaultCategory,
    checkIn: window.dayjs.today(),
    checkOut: window.dayjs.tomorrow(),
    adults: 2,
    children: 0,
    dogs: 0,
    lodgingType: defaultCategory?.defaultLodgingType,
    type: REGULAR_BOOKING,
    season: null,
    expected_arrival_time: null,
    cancellationPolicy: defaultCancellationPolicy,
    charges: [],
    appliedFees: [],
    accommodation_price: null
  }

  if (attributes.checkIn) {
    draft.checkIn = attributes.checkIn.clone()
  }

  if (attributes.checkOut) {
    draft.checkOut = attributes.checkOut.clone()
  }

  if (attributes.position) {
    draft.position = attributes.position
    draft.category = attributes.position.category
    draft.lodgingType = attributes.position.category.defaultLodgingType
  } else if (attributes.category) {
    draft.category = attributes.category
    draft.lodgingType = attributes.category.defaultLodgingType
  }

  if (attributes.customer) {
    draft.customer = attributes.customer
  }

  if (attributes.type) {
    draft.type = attributes.type
  }

  return draft
}

function createBookingGroupDraft (defaultCategory, attributes = {}) {
  const hasBookingAttributes = attributes.bookings?.length > 0
  const bookingDraftAttributes = hasBookingAttributes
    ? attributes.bookings
    : attributes.positions?.map(position => ({ position })) ?? []
  const sameDatesForAllBookings = !hasBookingAttributes || bookingAttributesHaveSameDates(bookingDraftAttributes)

  const bookings = bookingDraftAttributes.length > 0
    ? bookingDraftAttributes.map((bookingAttributes, index) => {
      const draft = createBookingDraft(defaultCategory, bookingAttributes)
      draft.clientId = index + 1
      return draft
    })
    : Array.from({ length: 2 }, (value, index) => {
      const draft = createBookingDraft(defaultCategory)
      draft.clientId = index + 1
      draft.position = defaultCategory?.positions?.[index] || null
      return draft
    })

  const bookingGroupDraft = {
    id: 0,
    name: '',
    type: GROUP_BOOKING,
    customer: attributes.customer || null,
    checkIn: window.dayjs.today(),
    checkOut: window.dayjs.tomorrow(),
    bookings,
    cancellationPolicy: bookings[0].cancellationPolicy,
    charges: [],
    appliedFees: [],
    accommodation_price: null,
    sameCancellationPolicyForAllBookings: true,
    sameDatesForAllBookings
  }

  if (attributes.checkIn) {
    bookingGroupDraft.checkIn = attributes.checkIn.clone()
    if (!hasBookingAttributes) {
      bookingGroupDraft.bookings.forEach(booking => {
        booking.checkIn = bookingGroupDraft.checkIn.clone()
      })
    }
  }

  if (attributes.checkOut) {
    bookingGroupDraft.checkOut = attributes.checkOut.clone()
    if (!hasBookingAttributes) {
      bookingGroupDraft.bookings.forEach(booking => {
        booking.checkOut = bookingGroupDraft.checkOut.clone()
      })
    }
  }

  return bookingGroupDraft
}

function bookingAttributesHaveSameDates (bookingAttributes) {
  return bookingsHaveSameDates(bookingAttributes.filter(booking => booking.checkIn && booking.checkOut))
}

function bookingsHaveSameDates (bookings) {
  if (bookings.length <= 1) {
    return true
  }

  const firstBooking = bookings[0]
  return bookings.every(booking => {
    return booking.checkIn?.isSame(firstBooking.checkIn, 'date') &&
      booking.checkOut?.isSame(firstBooking.checkOut, 'date')
  })
}

function ensureBookingDraft (context, type) {
  const { state } = context
  if (state.draft) {
    return Promise.resolve(state.draft)
  }

  return startNewBookingForm(context, { type })
    .then(() => state.draft)
}

// Pricing, validation, and save requests

const REQUIRED_CUSTOMER_MESSAGE = 'Kan inte gå vidare utan att välja kund'
const REQUIRED_POSITION_MESSAGE = 'Kan inte gå vidare utan att välja en plats'
const ADD_BOOKING_MUTATION = 'booking/ADD_BOOKING'
const TRIGGER_BOOKING_UPDATED_MUTATION = 'booking/TRIGGER_BOOKING_UPDATED'
const TRIGGER_RELOAD_SEASON_BOOKINGS_MUTATION = 'season/TRIGGER_RELOAD_SEASON_BOOKINGS'
const BOOKING_OPEN_DIALOG_BY_ID_ACTION = 'booking/openDialogById'
const CREATE_SNACKBAR_ACTION = 'snackbars/createSnackbar'

function resetPricingState (commit) {
  commit('INCREMENT_LATEST_PRICING_REQUEST_ID')
  resetRebookingPreviewState(commit)
  commit('SET_IS_FETCHING_CHARGES', false)
  commit('SET_IS_FETCHING_REBOOKING_PREVIEW', false)
  return true
}

function getCharges (context) {
  const { state, commit } = context
  if (!state.bookingType) {
    return false
  }

  if (isSeasonBookingType(state.bookingType)) {
    commit('SET_IS_FETCHING_CHARGES', false)
    return false
  }

  if (isGroupBookingType(state.bookingType)) {
    if (!state.bookingGroupDraft) {
      return false
    }

    const requestId = state.latestPricingRequestId + 1
    commit('SET_LATEST_PRICING_REQUEST_ID', requestId)
    commit('SET_IS_FETCHING_CHARGES', true)
    commit('SET_IS_FETCHING_REBOOKING_PREVIEW', false)

    if (state.bookingGroupDraft.bookings.some(bookingDraft => !bookingDraft.category)) {
      commit('SET_BOOKING_GROUP_CHARGES', [])
      commit('SET_IS_FETCHING_CHARGES', false)
      return false
    }

    return fetchLatestCharges({
      state,
      commit,
      requestId,
      setCharges: (commit, charges) => commit('SET_BOOKING_GROUP_CHARGES', charges),
      setFeeOptions: (commit, feeOptions) => commit('SET_BOOKING_GROUP_FEE_OPTIONS', feeOptions),
      request: () => RateService.getBookingGroupAccommodationCharges({ bookingGroup: state.bookingGroupDraft }),
      isStarted: true
    })
  }

  if (!state.draft?.cancellationPolicy || !state.draft?.category) {
    commit('SET_DRAFT_CHARGES', [])
    resetPricingState(commit)
    return false
  }

  resetRebookingPreviewState(commit)
  if (state.draft.id) {
    return getRebookingPreview(context)
  }

  const requestId = state.latestPricingRequestId + 1
  commit('SET_LATEST_PRICING_REQUEST_ID', requestId)
  const payload = pricingRequestPayload(state)
  return fetchLatestCharges({
    state,
    commit,
    requestId,
    setCharges: (commit, charges) => commit('SET_DRAFT_CHARGES', charges),
    setFeeOptions: (commit, feeOptions) => commit('SET_DRAFT_FEE_OPTIONS', feeOptions),
    request: () => RateService.getAccommodationCharges(payload),
    finishWhenDraftMissing: true
  })
}

function getRebookingPreview ({ state, commit }) {
  const requestId = state.latestPricingRequestId + 1
  commit('SET_LATEST_PRICING_REQUEST_ID', requestId)
  const payload = pricingRequestPayload(state)

  commit('SET_IS_FETCHING_REBOOKING_PREVIEW', true)
  commit('SET_IS_FETCHING_CHARGES', false)

  return RateService.getRebookingPreview(payload)
    .then(({ data }) => {
      if (requestId !== state.latestPricingRequestId) {
        return
      }

      if (data?.status === 'success') {
        const preview = data.data.preview
        commit('SET_DRAFT_FEE_OPTIONS', data.data.fee_options ?? [])
        commit(
          'SET_SELECTED_REBOOKING_OPTION',
          preview.rebooking_options.find(option => option.is_default) ?? preview.rebooking_options[0]
        )
        commit('SET_REBOOKING_PREVIEW', preview)
      } else {
        commit('SET_HAS_REBOOKING_PREVIEW_ERROR', true)
      }
    })
    .catch(() => {
      if (requestId === state.latestPricingRequestId) {
        commit('SET_HAS_REBOOKING_PREVIEW_ERROR', true)
      }
    })
    .finally(() => {
      if (requestId === state.latestPricingRequestId) {
        commit('SET_IS_FETCHING_REBOOKING_PREVIEW', false)
      }
    })
}

function pricingRequestPayload (state) {
  const booking = getActiveBookingDraft(state)

  return {
    bookingId: booking?.id,
    checkIn: booking?.checkIn,
    checkOut: booking?.checkOut,
    cancellationPolicyId: booking?.cancellationPolicy?.id,
    categoryId: booking?.category?.id,
    adults: booking?.adults,
    children: booking?.children,
    dogs: booking?.dogs,
    isApplyBookingFee: bookingFormIsApplyBookingFee(state),
    isApplyPositionReservationFee: bookingFormIsApplyPositionReservationFee(state),
    isApplyElectricityFee: bookingFormIsApplyElectricityFee(state)
  }
}

function fetchLatestCharges ({ state, commit, requestId, setCharges, setFeeOptions = null, request, finishWhenDraftMissing = false, isStarted = false }) {
  if (!isStarted) {
    commit('SET_IS_FETCHING_CHARGES', true)
    commit('SET_IS_FETCHING_REBOOKING_PREVIEW', false)
  }

  return request()
    .then(({ data }) => {
      if (requestId === state.latestPricingRequestId) {
        setCharges(commit, data?.status === 'success' ? data.data.charges : [])
        if (setFeeOptions) {
          setFeeOptions(commit, data?.status === 'success' ? data.data.fee_options ?? [] : [])
        }
      }
    })
    .catch(() => {
      if (requestId === state.latestPricingRequestId) {
        setCharges(commit, [])
        if (setFeeOptions) {
          setFeeOptions(commit, [])
        }
      }
    })
    .finally(() => {
      if ((finishWhenDraftMissing && !state.draft) || requestId === state.latestPricingRequestId) {
        commit('SET_IS_FETCHING_CHARGES', false)
      }
    })
}

function resetRebookingPreviewState (commit) {
  commit('SET_HAS_REBOOKING_PREVIEW_ERROR', false)
  commit('SET_REBOOKING_PREVIEW', null)
  commit('SET_SELECTED_REBOOKING_OPTION', null)
}

function clearBookingAvailabilityCache (commit, booking) {
  const params = bookingAvailabilityParams(booking, {
    checkIn: booking.checkIn,
    checkOut: booking.checkOut
  })

  const availabilityCacheMutations = [
    'position/DELETE_FETCHED_CATEGORY_AVAILABILITY',
    'position/DELETE_CATEGORY_AVAILABILITY_ERROR',
    'position/DELETE_FETCHED_AVAILABILITY',
    'position/DELETE_AVAILABILITY_ERROR'
  ]

  availabilityCacheMutations.forEach(mutation => {
    commit(mutation, params, { root: true })
  })
}

function saveExistingBookingForm ({ state, commit, dispatch }) {
  const booking = state.draft
  let didSave = false
  commit('SET_IS_SAVING', true)
  return BookingService.updateBooking({
    booking,
    rebookingOption: state.selectedRebookingOption,
    isApplyBookingFee: bookingFormIsApplyBookingFee(state),
    isApplyPositionReservationFee: bookingFormIsApplyPositionReservationFee(state),
    isApplyElectricityFee: bookingFormIsApplyElectricityFee(state)
  })
    .then(({ data }) => {
      if (data?.status !== 'success') {
        return false
      }

      didSave = true
      const savedBooking = data.data.booking
      commit('SET_SHOW_FORM_DIALOG', false)
      commit(TRIGGER_RELOAD_SEASON_BOOKINGS_MUTATION, null, { root: true })
      commit(TRIGGER_BOOKING_UPDATED_MUTATION, null, { root: true })

      if (savedBooking?.confirmed_at) {
        window.enrich.enrichBooking(savedBooking)
        commit(ADD_BOOKING_MUTATION, savedBooking, { root: true })
        return dispatch(BOOKING_OPEN_DIALOG_BY_ID_ACTION, {
          bookingId: savedBooking.id,
          refresh: true
        }, { root: true })
      }

      return false
    })
    .finally(() => {
      if (didSave) {
        clearBookingAvailabilityCache(commit, booking)
      }
      commit('SET_IS_SAVING', false)
    })
}

function saveNewBookingForm ({ state, rootState, commit, dispatch }) {
  const booking = state.draft
  const shouldCheckinAfterSave = state.shouldCheckinAfterSave
  const shouldSendBookingConfirmation = bookingFormShouldSendBookingConfirmation(state, rootState)
  let didSave = false

  commit('SET_IS_SAVING', true)
  return BookingService.saveBooking({
    booking,
    total: bookingFormChargeTotal(state),
    isApplyBookingFee: bookingFormIsApplyBookingFee(state),
    isApplyPositionReservationFee: bookingFormIsApplyPositionReservationFee(state),
    isApplyElectricityFee: bookingFormIsApplyElectricityFee(state)
  })
    .then(({ data }) => {
      if (data?.status !== 'success') {
        return false
      }

      didSave = true
      const savedBooking = data.data.booking
      commit('SET_SHOW_FORM_DIALOG', false)
      commit(TRIGGER_RELOAD_SEASON_BOOKINGS_MUTATION, null, { root: true })
      commit(TRIGGER_BOOKING_UPDATED_MUTATION, null, { root: true })

      if (!savedBooking?.confirmed_at) {
        return false
      }

      window.enrich.enrichBooking(savedBooking)
      commit(ADD_BOOKING_MUTATION, savedBooking, { root: true })

      if (shouldCheckinAfterSave) {
        return dispatch('booking/checkIn', { booking: savedBooking }, { root: true })
      }

      return dispatch(BOOKING_OPEN_DIALOG_BY_ID_ACTION, { bookingId: savedBooking.id }, { root: true })
        .then(() => {
          if (shouldSendBookingConfirmation) {
            return dispatch('booking/sendBookingConfirmation', { bookingId: savedBooking.id }, { root: true })
          }

          return false
        })
    })
    .finally(() => {
      if (didSave) {
        clearBookingAvailabilityCache(commit, booking)
      }
      commit('SET_IS_SAVING', false)
    })
}

function bookingFormChargeTotal (state) {
  const chargesTarget = isGroupBookingType(state.bookingType)
    ? state.bookingGroupDraft
    : state.draft

  if (!chargesTarget) {
    return 0
  }

  const accommodationPrice = isGroupBookingType(state.bookingType)
    ? 0
    : state.draft?.accommodation_price ?? 0

  return chargesTarget.charges.reduce((total, charge) => total + charge.total, 0) + accommodationPrice
}

function bookingFormFeeOptions (state) {
  if (!isGroupBookingType(state.bookingType)) {
    return state.draft?.feeOptions ?? state.draft?.category?.fee_options ?? []
  }

  if (state.bookingGroupDraft?.feeOptions?.length) {
    return state.bookingGroupDraft.feeOptions
  }

  const feeOptionsByKey = new Map()
  state.bookingGroupDraft?.bookings.forEach(booking => {
    const feeOptions = booking.feeOptions ?? booking.category?.fee_options ?? []
    feeOptions.forEach(feeOption => {
      if (!feeOptionsByKey.has(feeOption.key)) {
        feeOptionsByKey.set(feeOption.key, feeOption)
      }
    })
  })

  return Array.from(feeOptionsByKey.values())
    .filter(feeOption => feeOption.key === BOOKING_FEE)
}

function bookingFormIsApplyBookingFee (state) {
  const booking = getActiveBookingDraft(state)
  if (booking?.id && booking.isBookingFeeKnown === false) {
    // På kalendersidan vet vi inte om bokningen har bokningsavgift eller inte, den ska exkluderas från preview och ska inte togglas när redigerar bokning därifrån
    return null
  }

  return !!booking &&
    (booking.appliedFees ?? []).includes(BOOKING_FEE)
}

function bookingFormIsApplyPositionReservationFee (state) {
  return (getActiveBookingDraft(state)?.appliedFees ?? []).includes(POSITION_RESERVATION_FEE)
}

function bookingFormIsApplyElectricityFee (state) {
  const booking = getActiveBookingDraft(state)
  return !!booking &&
    (booking.appliedFees ?? []).includes(ELECTRICITY_FEE)
}

function bookingFormShouldSendBookingConfirmation (state, rootState) {
  return rootState.localSettings.bookingFormAlwaysSendConfirmationEmail &&
    !isExistingFormDraft(state) &&
    !isDropInFormDraft(state) &&
    !isSeasonBookingType(state.bookingType)
}
